import React, { useEffect, useState } from 'react';
import { auth,  } from '../../../firebase';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Select, MenuItem } from '@mui/material';
import { Dialog, DialogActions, DialogTitle, Button, Chip } from '@mui/material';
import ShopNavbar from './ShopNavbar';
import { getItemsByShopEmail, updateComplaintStatus, listenToComplaints } from '../../DatabaseAPI/ShopPageAPI';
import {
  COMPLAINT_STATUS_OPTIONS,
  normalizeComplaintStatus,
} from '../../../utils/complaintStatus';

const ShopPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const userRole = (sessionStorage.getItem("userRole") || "").toLowerCase();
  const isSeller = ["seller", "operator", "facility_operator", "shop"].includes(userRole);
  const [closeRequestDialogOpen, setCloseRequestDialogOpen] = useState(false);
  const [closeRequestComplaintId, setCloseRequestComplaintId] = useState(null);

  /*
    This useEffect hook monitors changes in the user's authentication state.
    When the user logs in or out, it updates the state with the user's email.
    The `unsubscribe` function cleans up the listener when the component unmounts.
   */
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentUserEmail(user.email);
      }
    });

    return () => unsubscribe();
  }, []);

  /*
    This useEffect hook listens for changes in the `currentUserEmail` state.
    When `currentUserEmail` is available, it fetches items related to that email from the database.
    The function sets up a listener to update the items list and also a cleanup function.
   */

  useEffect(() => {
    let unsubscribeItems;
    if (currentUserEmail) {
      setLoading(true);
      unsubscribeItems = getItemsByShopEmail(currentUserEmail, (itemsWithUserDetails) => {
        setItems(itemsWithUserDetails);
        setLoading(false);
      });
    }

    return () => {
      if (unsubscribeItems) {
        unsubscribeItems();
      }
    };
  }, [currentUserEmail]);

  useEffect(() => {
    const unsubscribeComplaints = listenToComplaints((complaintsData) => {
      setItems(prevItems => {
        return prevItems.map(item => ({
          ...item,
          complaints: complaintsData.filter(complaint => complaint.itemId === item.id),
        }));
      });
    });

    return () => unsubscribeComplaints();
  }, []);

/*
  Handles the change in complaint status when the user selects a new status from a dropdown.
  It updates the status of the complaint in the database.
 
  @param {string} complaintId - The ID of the complaint to update.
  @param {object} event - The event object from the dropdown change.
 */
  const handleStatusChange = async (complaintId, event) => {
    const newStatus = event.target.value;

    const currentComplaint = items
      .flatMap((item) => item.complaints || [])
      .find((complaint) => complaint.id === complaintId);

    const currentStatus = normalizeComplaintStatus(currentComplaint?.status);
    const nextStatus = normalizeComplaintStatus(newStatus);

    if (
      currentComplaint?.closeRequestApproved === true ||
      currentComplaint?.closeRequestApprovedAt ||
      currentComplaint?.closeRequestApprovedBy
    ) {
      return;
    }

    if (isSeller) {
      if (currentStatus === "In Progress" && nextStatus === "Open") {
        return;
      }

      if (currentStatus === "In Progress" && nextStatus === "Resolved") {
        setCloseRequestComplaintId(complaintId);
        setCloseRequestDialogOpen(true);
        return;
      }
    }

    await updateComplaintStatus(complaintId, newStatus);
    console.log(`Updated complaint ${complaintId} to status: ${newStatus}`);
  };

  const handleCloseRequestCancel = () => {
    setCloseRequestDialogOpen(false);
    setCloseRequestComplaintId(null);
  };

  const handleRaiseCloseRequest = async () => {
    if (!closeRequestComplaintId) {
      return;
    }

    await updateComplaintStatus(closeRequestComplaintId, "In Progress", {
      closeRequestPending: true,
      closeRequestRequestedBy: auth.currentUser?.email || auth.currentUser?.uid || null,
      closeRequestRequestedAt: new Date(),
    });

    setItems((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        complaints: (item.complaints || []).map((complaint) =>
          complaint.id === closeRequestComplaintId
            ? { ...complaint, closeRequestPending: true }
            : complaint
        ),
      }))
    );

    handleCloseRequestCancel();
  };

  const getAllowedStatusOptions = (complaint) => {
    const currentStatus = normalizeComplaintStatus(complaint?.status);

    if (isSeller && currentStatus === "In Progress") {
      return COMPLAINT_STATUS_OPTIONS.filter((status) => normalizeComplaintStatus(status) !== "Open");
    }

    return COMPLAINT_STATUS_OPTIONS;
  };

  

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ flexGrow: 1, position: 'relative', width: '98%', left: 16, right: 16, paddingBottom: 85,  paddingTop: 0 }}>
      <ShopNavbar />
      <div style={{ fontWeight: 'bold', fontSize: '24px', paddingBottom: 30 }}>Assigned Customers and Complaints</div>
    <TableContainer component={Paper} style={{ flexGrow: 1, paddingBottom: 40 }}>
      <Table>
        <TableHead>
          <TableRow>
          <TableCell style={{ textAlign: 'center', border: '1px solid #ddd', borderBottom: '1px solid black' }}>Warranty Card Image</TableCell>
            <TableCell style={{ textAlign: 'center', border: '1px solid #ddd', borderBottom: '1px solid black' }}>Item Make</TableCell>
            <TableCell style={{ textAlign: 'center', border: '1px solid #ddd', borderBottom: '1px solid black' }}>Customer Name</TableCell>
            <TableCell style={{ textAlign: 'center', border: '1px solid #ddd', borderBottom: '1px solid black' }}>Customer Email</TableCell>
            <TableCell style={{ textAlign: 'center', border: '1px solid #ddd', borderBottom: '1px solid black' }}>Product Name</TableCell>
            <TableCell style={{ textAlign: 'center', border: '1px solid #ddd', borderBottom: '1px solid black' }}>Purchase Date</TableCell>
            <TableCell style={{ textAlign: 'center', border: '1px solid #ddd', borderBottom: '1px solid black' }}>Warranty Expiry Date</TableCell>
            <TableCell style={{ textAlign: 'center', border: '1px solid #ddd', borderBottom: '1px solid black' }}>Complaints</TableCell>
            <TableCell style={{ textAlign: 'center', border: '1px solid #ddd', borderBottom: '1px solid black' }}>Complaint Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell style={{ textAlign: 'center', borderRight: '1px solid #ddd' }}>
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.itemMake}
                    style={{ width: '100px', height: 'auto', objectFit: 'cover' }}
                  />
                ) : (
                  <span>Image unavailable</span>
                )}
              </TableCell>
              <TableCell style={{ textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.itemMake}</TableCell>
              <TableCell style={{ textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.userData ? item.userData.displayName : 'N/A'}</TableCell>
              <TableCell style={{ textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.userData ? item.userData.email : 'N/A'}</TableCell>
              <TableCell style={{ textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.productName}</TableCell>
              <TableCell style={{ textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.purchaseDate || 'N/A'}</TableCell>
              <TableCell style={{ textAlign: 'center', borderRight: '1px solid #ddd' }}>{item.warrantyExpiryDate || 'N/A'}</TableCell>
              <TableCell style={{ textAlign: 'center', borderRight: '1px solid #ddd' }}>
                {item.complaints.length > 0 ? (
                  item.complaints.map(complaint => (
                    <div key={complaint.id} style={{ marginBottom: '10px' }}>
                    <div>
                      <strong>Complaint ID:</strong> {complaint.id}
                    </div>
                    <div>
                    <strong>Description: </strong>{complaint.issueDescription}
                    </div>
                  </div>
                  ))
                ) : (
                  <span>N/A</span>
                )}
              </TableCell>

              <TableCell>
  {item.complaints.length > 0 ? (
    item.complaints.map((complaint, index) => (
      <div key={complaint.id} style={{ marginBottom: '10px' }}>
        {complaint.closeRequestApproved === true || complaint.closeRequestApprovedAt || complaint.closeRequestApprovedBy ? (
          <Chip label="Resolved" color="success" size="small" style={{ marginLeft: '10px', marginBottom: '10px' }} />
        ) : complaint.closeRequestPending ? (
          <Chip label="Close Requested" color="warning" size="small" style={{ marginLeft: '10px', marginBottom: '10px' }} />
        ) : (
          <Select
            value={normalizeComplaintStatus(complaint.status)}
            onChange={(event) => handleStatusChange(complaint.id, event)}
            style={{ marginLeft: '10px', marginBottom: '10px' }}
          >
            {getAllowedStatusOptions(complaint).map((status) => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
        )}
      </div>
    ))
  ) : (
    <Select
      value={'N/A'}
      style={{ marginLeft: '10px' }}
    >
      <MenuItem value="N/A">N/A</MenuItem>
    </Select>
  )}
</TableCell>

            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <Dialog open={closeRequestDialogOpen} onClose={handleCloseRequestCancel}>
      <DialogTitle>Raise close request</DialogTitle>
      <DialogActions style={{ justifyContent: 'center', paddingBottom: '16px', gap: '8px' }}>
        <Button onClick={handleCloseRequestCancel}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleRaiseCloseRequest}>
          Close Complaint
        </Button>
      </DialogActions>
    </Dialog>
    </div>
  );
};

export default ShopPage;
